import { Button, Form, Spinner } from 'react-bootstrap';
import { CustomerProjectCoCCsv, VCOProject, VCOProjectCoC } from './CustomProjectModels';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import { AddFilled } from '@carbon/icons-react';
import ApiHelper from '../../../Shared/ApiHelper';
import DeleteButton from '../../../Shared/components/DeleteButton';
import SaveButton from '../../../Shared/components/SaveButton';
import Select from 'react-select';
import { SelectOption } from '../../../Shared/SharedModels';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface ProjectCoCEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

function getProjectCoCOptionLabel(csv: CustomerProjectCoCCsv): string {
    return `Project CoC ID: ${csv.projectCoCID || 'New Project CoC'}`;
}

const ProjectCoCEditForm: FunctionComponent<ProjectCoCEditFormProps> = (props: ProjectCoCEditFormProps) => {
    const { vcoProject, batchKey } = props;

    const userProfile = useContext(UserProfileContext);

    const defaultProjectCoCCsv = useMemo<CustomerProjectCoCCsv>(
        () => ({
            batchKey: 0,
        }),
        []
    );

    const [selectedProjectCoC, setSelectedProjectCoC] = useState<CustomerProjectCoCCsv | undefined>();
    const [projectCoCOptions, setProjectCoCOptions] = useState<SelectOption<CustomerProjectCoCCsv>[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [canSave, setCanSave] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);

    const { instance } = useMsal();

    useEffect(() => {
        setCanSave(!!selectedProjectCoC?.coCCode);
    }, [selectedProjectCoC?.coCCode, selectedProjectCoC?.city]);

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + `/CustomProject/projects/${vcoProject.projectKey}/projectCoC`,
                'GET'
            )
                .then(async (result: Response) => {
                    const projectCoCs: VCOProjectCoC[] = await result.json();
                    const customProjectCoCs: CustomerProjectCoCCsv[] = projectCoCs.map<CustomerProjectCoCCsv>((vco) => {
                        const csv: CustomerProjectCoCCsv = {
                            batchKey: batchKey,
                            projectCoCID: vco.projectCoCID,
                            sourceSystemShortName:
                                userProfile.sourceSystems.find((s) => s.sourceSystemKey === vcoProject.sourceSystemKey)
                                    ?.sourceSystemShortName ?? '',
                            projectID: vco.projectID ?? '',
                            coCCode: vco.coCCode,
                            geocode: vco.geocode,
                            address1: vco.address1,
                            address2: vco.address2,
                            city: vco.city,
                            state: vco.state,
                            zip: vco.zip,
                            geographyType: vco.geographyTypeKey,
                            dateCreated: vco.dateCreated,
                            dateUpdated: vco.dateUpdated,
                            userID: vco.userID,
                            dateDeleted: vco.dateDeleted,
                        };

                        return csv;
                    });

                    if (customProjectCoCs.length) {
                        setProjectCoCOptions(
                            customProjectCoCs.map((csv) => ({
                                label: getProjectCoCOptionLabel(csv),
                                value: csv,
                            }))
                        );

                        setSelectedProjectCoC(customProjectCoCs[0]);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        api,
        batchKey,
        instance,
        isLoading,
        userProfile.sourceSystems,
        vcoProject.projectKey,
        vcoProject.sourceSystemKey,
    ]);

    return (
        <div className="project-card mb-5">
            <div className="project-card-header">
                <h3>Project CoC CSV Data</h3>

                {!!batchKey && !isLoading && (
                    <div className="flex-row">
                        {selectedProjectCoC?.projectCoCID && (
                            <DeleteButton
                                isLoading={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const dateDeleted = new Date().toISOString();

                                    const payload: CustomerProjectCoCCsv = {
                                        ...selectedProjectCoC,
                                        dateDeleted: dateDeleted,
                                        dateUpdated: dateDeleted,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/projectCoC`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Project CoC Data Deleted Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}

                        <Button
                            className="form-action-button mr-2 ml-2"
                            variant="outline-primary"
                            disabled={isSaving}
                            onClick={() => {
                                const currentDate = new Date().toISOString().split('T')[0];
                                const newProjectCoC: CustomerProjectCoCCsv = {
                                    batchKey: batchKey,
                                    sourceSystemShortName:
                                        userProfile.sourceSystems.find(
                                            (s) => s.sourceSystemKey === vcoProject.sourceSystemKey
                                        )?.sourceSystemShortName ?? '',
                                    projectID: vcoProject.projectId ?? '',
                                    dateCreated: currentDate,
                                    dateUpdated: currentDate,
                                    userID: vcoProject.userID,
                                };

                                setSelectedProjectCoC(newProjectCoC);
                            }}
                        >
                            <AddFilled size={24} className="button-icon-padding" /> Add Project CoC
                        </Button>

                        {selectedProjectCoC && (
                            <SaveButton
                                isSaving={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const payload: CustomerProjectCoCCsv = {
                                        ...selectedProjectCoC,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/projectCoC`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Project CoC Data Saved Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <Spinner
                    className="centered-spinner"
                    variant="primary"
                    size="sm"
                    animation="border"
                    data-testid="projectCoCEditFormSpinner"
                />
            ) : (
                <div className="project-card-body">
                    {projectCoCOptions.length || selectedProjectCoC ? (
                        <>
                            <Form.Group className="wide-card-form-group">
                                <Form.Label className={isSaving ? 'disabled-label' : ''}>Project CoC ID</Form.Label>
                                <Select
                                    isClearable
                                    isLoading={isLoading}
                                    isDisabled={isSaving}
                                    className="filterSelect"
                                    placeholder="Select Project CoC..."
                                    value={
                                        selectedProjectCoC
                                            ? {
                                                  label: getProjectCoCOptionLabel(selectedProjectCoC),
                                                  value: selectedProjectCoC,
                                              }
                                            : undefined
                                    }
                                    options={projectCoCOptions}
                                    onChange={(selectedOption) => {
                                        setSelectedProjectCoC(selectedOption?.value);
                                    }}
                                />
                            </Form.Group>

                            <Form className="card-form">
                                {selectedProjectCoC && (
                                    <>
                                        <Form.Group className="card-form-group">
                                            <Form.Label>CoC Code*</Form.Label>
                                            <Form.Control
                                                data-testid="cocCodeInput"
                                                placeholder="Enter CoC Code"
                                                isInvalid={!selectedProjectCoC.coCCode}
                                                value={selectedProjectCoC.coCCode ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;

                                                    if (value.length <= 6) {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                coCCode: value,
                                                            };
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                        <Form.Group className="card-form-group">
                                            <Form.Label>Geocode</Form.Label>
                                            <Form.Control
                                                data-testid="geocodeInput"
                                                placeholder="Enter Geocode"
                                                value={selectedProjectCoC.geocode ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber && event.target.value.length <= 6) {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                geocode: numberValue.toString(),
                                                            };
                                                        });
                                                    } else if (event.target.value === '') {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                geocode: undefined,
                                                            };
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>Address 1</Form.Label>
                                            <Form.Control
                                                data-testid="address1Input"
                                                placeholder="Enter Address 1"
                                                value={selectedProjectCoC.address1 ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    setSelectedProjectCoC((prev) => {
                                                        return {
                                                            ...defaultProjectCoCCsv,
                                                            ...prev,
                                                            address1: value,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>City</Form.Label>
                                            <Form.Control
                                                data-testid="cityInput"
                                                placeholder="Enter City"
                                                value={selectedProjectCoC.city ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    setSelectedProjectCoC((prev) => {
                                                        return {
                                                            ...defaultProjectCoCCsv,
                                                            ...prev,
                                                            city: value,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>State</Form.Label>
                                            <Form.Control
                                                data-testid="stateInput"
                                                placeholder="Enter State"
                                                value={selectedProjectCoC.state ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;

                                                    if (value.length <= 2) {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                state: value,
                                                            };
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>ZIP Code</Form.Label>
                                            <Form.Control
                                                data-testid="zipInput"
                                                placeholder="Enter ZIP Code"
                                                value={selectedProjectCoC.zip ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber && event.target.value.length <= 5) {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                zip: numberValue.toString(),
                                                            };
                                                        });
                                                    } else if (event.target.value === '') {
                                                        setSelectedProjectCoC((prev) => {
                                                            return {
                                                                ...defaultProjectCoCCsv,
                                                                ...prev,
                                                                zip: undefined,
                                                            };
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </Form>
                        </>
                    ) : (
                        <p>No project CoC records found</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectCoCEditForm;
